/**
 * Oynatma tercihleri — kalite ve yöntem (ADR-011).
 *
 * ## Neden kullanıcı seçebilsin?
 *
 * Karar motoru iki şeyi birden karar veriyordu: **hangi yol** (doğrudan
 * oynatma mı, dönüştürme mi) ve **hangi kalite**. Oysa bu ikisi farklı
 * gerekçelerle seçilir:
 *
 *   - **Yol** → sunucunun yükü. Doğrudan oynatmada sunucu **hiç** CPU
 *     kullanmaz; dönüştürmede VPS'in CPU'su (GPU yok) tam yük çalışır.
 *   - **Kalite** → cihazın ve bağlantının taşıyabildiği.
 *
 * Ev kullanıcısı için "sunucuyu yormamak" ve "telefonda orijinal kalitede
 * izlemek" birbirine zıt iki istek. Karar motoru ikisini birleştirip ikisini
 * de gölgeliyordu: telefonda 720p'ye düşüyordu (dönüştürme → CPU yükü),
 * masaüstünde ise profil tavanı işlemiyordu.
 *
 * Artık kullanıcı seçiyor:
 *
 * | Tercih | Anlamı | Sunucu yükü |
 * |---|---|---|
 * | `quality: 'auto'` | Sunucu karar versin (varsayılan) | değişir |
 * | `quality: 'original'` | Kaynak olduğu gibi | **yok** (doğrudan gönderilir) |
 * | `quality: '1080p' \| '720p' \| '480p'` | Belirtilen yükseklikte dönüştür | **yüksek** (x264 CPU) |
 * | `mode: 'auto'` | Sunucu karar versin | — |
 * | `mode: 'direct'` | Asla dönüştürme | **yok**; oynatılamıyorsa hata verilir |
 * | `mode: 'transcode'` | Her zaman dönüştür | **yüksek** |
 *
 * ## Neden `localStorage`?
 *
 * Tercih **cihaza** aittir: telefonda "orijinal", masaüstünde "720p" seçmek
 * makul. Sunucuda tutmak yerine cihazda tutulur ve karar isteğinde iletilir;
 * sunucu kalıcı bir "kullanıcı profili" tutmaz.
 *
 * ## Güvenlik sınırı
 *
 * Tercih **dönüştürmeyi yasaklayabilir** (`mode: 'direct'`), ama **dönüştürme
 * sınırını kaldıramaz**: sunucunun kendi yetenekleri (HEVC kodlayamaz, disk
 * kotası, eşzamanlı iş sınırı) her zaman geçerlidir.
 */
import { useCallback, useState } from 'react';
import { recordSwallowed } from './swallowed.ts';

export type PlaybackQuality = 'auto' | 'original' | '1080p' | '720p' | '480p';
export type PlaybackMode = 'auto' | 'direct' | 'transcode';

export interface PlaybackPrefs {
  quality: PlaybackQuality;
  mode: PlaybackMode;
}

export const DEFAULT_PLAYBACK_PREFS: PlaybackPrefs = { quality: 'auto', mode: 'auto' };

/** Seçeneklerin görünen adları (arayüzde kullanılır). */
export const QUALITY_LABELS: Record<PlaybackQuality, string> = {
  auto: 'Otomatik',
  original: 'Orijinal (doğrudan)',
  '1080p': '1080p',
  '720p': '720p',
  '480p': '480p',
};

export const MODE_LABELS: Record<PlaybackMode, string> = {
  auto: 'Otomatik',
  direct: 'Doğrudan (CPU yükü yok)',
  transcode: 'Dönüştür (CPU yükü var)',
};

/**
 * Seçilen kalite sunucuyu **zorlar mı?**
 *
 * `original` ve `auto` doğrudan oynatmayı mümkün kıldığında sunucu hiç
 * yorulmaz; belirli bir yükseklik seçilirse dönüştürme yapılır. Arayüz bu
 * bilgiyi kullanıcıya gösterir ("sunucunun CPU'su yoğun kullanılacak").
 */
export function qualityForcesTranscode(quality: PlaybackQuality): boolean {
  return quality === '1080p' || quality === '720p' || quality === '480p';
}

/**
 * Etkin tercihler = kalıcı tercih + oturum geçersiz kılması.
 *
 * ## Neden ikisi?
 *
 * "Bağlantı yavaş → 720p'ye düşür" eylemi **yalnız bu oynatmayı** etkiler;
 * kalıcı tercih yalnız tercih panelinden değişir.
 *
 * Daha önce buton kalıcı tercihi doğrudan değiştiriyordu: kullanıcı bir kez
 * tıkladığında cihaz 720p'e kilitleniyor ve sonraki tüm filmler 720p hedefiyle
 * açılıyordu. Kullanıcı "bu film yavaş açıldı" demişti, "cihazım hep 720p
 * olsun" dememişti.
 *
 * Saf fonksiyon: bileşen testi yazılabilsin diye (bileşenin kendisi 1500
 * satırlık bir ekran).
 */
export function effectivePlaybackPrefs(
  persisted: PlaybackPrefs,
  sessionQuality: PlaybackQuality | null,
): PlaybackPrefs {
  return {
    quality: sessionQuality ?? persisted.quality,
    mode: persisted.mode,
  };
}

const STORAGE_KEY = 'pbfork.playbackPrefs.v1';

/** Saklanan değerler geçerli mi? Geçersizse varsayılana düşer. */
export function sanitizePlaybackPrefs(input: Partial<PlaybackPrefs>): PlaybackPrefs {
  const quality = input.quality;
  const mode = input.mode;
  return {
    quality:
      quality === 'original' ||
      quality === '1080p' ||
      quality === '720p' ||
      quality === '480p' ||
      quality === 'auto'
        ? quality
        : DEFAULT_PLAYBACK_PREFS.quality,
    mode:
      mode === 'direct' || mode === 'transcode' || mode === 'auto'
        ? mode
        : DEFAULT_PLAYBACK_PREFS.mode,
  };
}

function readStored(): Partial<PlaybackPrefs> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as Partial<PlaybackPrefs>;
  } catch (error) {
    recordSwallowed('playbackPrefs', error);
    return {};
  }
}

export interface PlaybackPrefsApi {
  prefs: PlaybackPrefs;
  setQuality: (quality: PlaybackQuality) => void;
  setMode: (mode: PlaybackMode) => void;
  /** Varsayılana dön (Otomatik/Otomatik). */
  reset: () => void;
}

export function usePlaybackPrefs(): PlaybackPrefsApi {
  const [prefs, setPrefs] = useState<PlaybackPrefs>(() => ({
    ...DEFAULT_PLAYBACK_PREFS,
    ...sanitizePlaybackPrefs(readStored()),
  }));

  const update = useCallback((patch: Partial<PlaybackPrefs>) => {
    setPrefs((previous) => {
      const next = sanitizePlaybackPrefs({ ...previous, ...patch });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        recordSwallowed('playbackPrefs', error);
        // Depolama kapalı; tercih yine de bu oturumda geçerli.
      }
      return next;
    });
  }, []);

  return {
    prefs,
    setQuality: (quality) => update({ quality }),
    setMode: (mode) => update({ mode }),
    reset: () => update(DEFAULT_PLAYBACK_PREFS),
  };
}
